import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';
import { ArrowBackIosNew, Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

interface ZohoModalProps {
  openModal: boolean;
  setConfigModals: React.Dispatch<
    React.SetStateAction<{
      whatsapp: boolean;
      telegram: boolean;
      slack: boolean;
      hubspot: boolean;
      twilio: boolean;
      zoho: boolean;
    }>
  >;
}

function ZohoConfigModal(props: ZohoModalProps) {
  const { openModal, setConfigModals } = props;

  const setStoreState = useAlchemystStoreForAi((store) => store.setStoreState);
  const snackbarEntries = useAlchemystStoreForAi(
    (store) => store.snackbarEntries
  );

  const [formValues, setFormValues] = useState({
    code: '',
    clientId: '',
    clientSecret: '',
    apiKey: '',
  });
  const [savingIntegration, setSavingIntegration] = useState(false);
  const [responseData, setResponseData] = useState('');
  const [copied, setCopied] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);
  const [connectedAccounts, setConnectedAccounts] = useState<
    {
      clientId: string;
      userId: string;
      createdAt?: string;
    }[]
  >([]);
  const [removeZoho, setRemoveZoho] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(responseData).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const closeModal = () => {
    setModalIndex(0);
    setResponseData('');
    setConfigModals((prev) => ({
      ...prev,
      zoho: false,
    }));
  };

  const handleFormSubmit = async () => {
    setSavingIntegration(true);
    try {
      const response = await fetchWithRewrites(
        `/api/v1/persona/maya/integrations/zoho/init`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formValues),
        }
      );

      if (!response.ok) {
        throw new Error(`Error: ${response.status} - ${response.statusText}`);
      }

      const result = await response.json();
      // console.log('Response from Zoho API:', result);

      // Store the response data for display (if needed)
      if (result) setResponseData(JSON.stringify(result, null, 2));

      setFormValues((prev) => ({
        ...prev,
        code: '',
        clientId: '',
        clientSecret: '',
        apiKey: '',
      }));

      fetchConnectedAccounts();
      setStoreState({
        snackbarEntries: [
          ...snackbarEntries,
          {
            severity: 'success',
            message: 'Zoho account connected successfully!',
            type: 'alert',
            notificationId: crypto.randomUUID(),
          },
        ],
      });
    } catch (error) {
      console.error('Error while saving Zoho integration:', error);
    } finally {
      setSavingIntegration(false);
    }
  };

  const saveIntegrationToBackend = async (
    formData: typeof formValues,
    authResponse: any
  ) => {
    try {
      const response = await fetchWithRewrites(
        `/api/v1/persona/maya/integrations/zoho/init`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...formData,
            authResponse,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Error saving to backend: ${response.status} - ${response.statusText}`
        );
      }

      const result = await response.json();
      console.log('Saved to backend:', result);
    } catch (error) {
      console.error('Error saving integration to backend:', error);
    }
  };

  const removeIntegration = async (clientId: string, userId: string) => {
    try {
      setRemoveZoho(true);
      const res = await fetchWithRewrites(
        '/api/v1/persona/maya/integrations/zoho',
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ clientId, userId }),
        }
      );

      if (res.ok) {
        fetchConnectedAccounts();
        console.log('DELETED');
      }
    } catch (error) {
      console.error('Error removing integration:', error);
    } finally {
      setRemoveZoho(false);
    }
  };

  const fetchConnectedAccounts = async () => {
    try {
      const res = await fetchWithRewrites(
        '/api/v1/persona/maya/integrations/zoho/fetch'
      );
      const data = await res.json();

      setConnectedAccounts(
        data.data.map(
          (item: {
            credentials: {
              clientId: string;
              userId: string;
              createdAt?: string;
            };
          }) => item.credentials
        )
      );
    } catch (error) {
      console.error('Error fetching connected accounts:', error);
      setConnectedAccounts([]);
    }
  };

  useEffect(() => {
    fetchConnectedAccounts();
  }, []);

  return (
    <Modal
      aria-describedby="zoho-integration-settings-modal-description"
      aria-labelledby="zoho-integration-settings-modal"
      onClose={closeModal}
      open={openModal}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Box>
          {modalIndex === 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 4,
              }}
            >
              <Typography variant="h5">Zoho Accounts Connected</Typography>
              <Button
                onClick={() => {
                  setModalIndex(1);
                }}
              >
                Add
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                pb: 4,
              }}
            >
              <Button
                onClick={() => {
                  setModalIndex(0);
                }}
              >
                <ArrowBackIosNew />
              </Button>
              <Typography variant="h5">Add New Zoho Account</Typography>
            </Box>
          )}
        </Box>
        <Box>
          {modalIndex === 0 ? (
            <Box
              sx={{
                maxHeight: 500,
                overflowY: 'scroll',
              }}
            >
              {connectedAccounts.length > 0 ? (
                connectedAccounts.map((integration, idx) => (
                  <Card key={idx} sx={{ mb: 2, borderRadius: 2 }}>
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 2,
                        }}
                      >
                        <Typography variant="h6">
                          Client ID: {integration.clientId}
                        </Typography>
                      </Box>

                      {/* <Box sx={{ mb: 3 }}>
                        <Typography sx={{ mb: 1 }} variant="subtitle2">
                          User ID
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            InputProps={{ readOnly: true }}
                            fullWidth
                            size="small"
                            value={integration.userId}
                          />
                          <IconButton
                            onClick={() => {
                              navigator.clipboard.writeText(integration.userId);
                            }}
                            size="small"
                          >
                            <ContentCopy />
                          </IconButton>
                        </Box>
                      </Box> */}

                      {integration.createdAt && (
                        <Typography variant="caption" color="text.secondary">
                          Connected:{' '}
                          {new Date(integration.createdAt).toLocaleDateString()}
                        </Typography>
                      )}
                    </CardContent>

                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        color="error"
                        startIcon={
                          removeZoho ? (
                            <CircularProgress size={24} />
                          ) : (
                            <Delete />
                          )
                        }
                        disabled={removeZoho}
                        onClick={() => {
                          removeIntegration(
                            integration.clientId,
                            integration.userId
                          );
                        }}
                        variant="contained"
                      >
                        Remove Integration
                      </Button>
                    </CardActions>
                  </Card>
                ))
              ) : (
                <Typography sx={{ textAlign: 'center', mt: 3 }} variant="h6">
                  No connected Zoho accounts. Please create one.
                </Typography>
              )}
            </Box>
          ) : (
            <Box>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Authorization Code"
                  onChange={(e) => {
                    handleInputChange('code', e.target.value);
                  }}
                  value={formValues.code}
                  variant="outlined"
                  helperText="The authorization code received from Zoho OAuth"
                />
                <TextField
                  fullWidth
                  label="Client ID"
                  onChange={(e) => {
                    handleInputChange('clientId', e.target.value);
                  }}
                  value={formValues.clientId}
                  variant="outlined"
                  helperText="Your Zoho application's client ID"
                />
                <TextField
                  fullWidth
                  label="Client Secret"
                  type="password"
                  onChange={(e) => {
                    handleInputChange('clientSecret', e.target.value);
                  }}
                  value={formValues.clientSecret}
                  variant="outlined"
                  helperText="Your Zoho application's client secret"
                />
                <TextField
                  fullWidth
                  label="Platform Api Key"
                  onChange={(e) => {
                    handleInputChange('apiKey', e.target.value);
                  }}
                  value={formValues.apiKey}
                  variant="outlined"
                />
                <Button
                  color="primary"
                  disabled={savingIntegration}
                  onClick={handleFormSubmit}
                  startIcon={
                    savingIntegration ? <CircularProgress size={24} /> : null
                  }
                  variant="contained"
                >
                  {savingIntegration ? 'Connecting...' : 'Connect to Zoho'}
                </Button>
              </Stack>
              {/* {responseData.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography sx={{ mb: 1 }} variant="subtitle2">
                    API Response
                  </Typography>
                  <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                  >
                    <TextField
                      InputProps={{
                        readOnly: true,
                      }}
                      fullWidth
                      multiline
                      rows={6}
                      value={responseData}
                      variant="outlined"
                      sx={{
                        '& .MuiInputBase-input': {
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                    <Tooltip title={copied ? 'Copied!' : 'Copy Response'}>
                      <IconButton color="primary" onClick={handleCopy}>
                        {copied ? (
                          <CheckCircleOutline color="success" />
                        ) : (
                          <ContentCopy />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )} */}
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}

export default ZohoConfigModal;
